//Manages export and archive contact List actions
import { useState, useCallback } from "react"
import { useToast } from "@/hooks/use-toast"
import { apiFetch } from "@/lib/api/apiFetch"

interface Contact {
  id: string
  email: string
  name: string
  [key: string]: any
}

interface UseContactActionsReturn {
  exporting: boolean
  archivingContacts: Set<string>
  handleExport: (search?: string, status?: string, source?:string, location?: string, lists?: string, emailStatus?: string) => Promise<void>
  handleArchive: (contact: Contact) => Promise<boolean>
}

/**
 * Custom React hook to manage Contact export and archive actions.
 *
 * Responsibilities:
 * - Exporting the contact list as a downloadable CSV file
 * - Archiving individual contacts
 * - Tracking export and per-contact archive loading states
 * - Displaying success and error notifications via toast messages
 *
 * This hook encapsulates all UI-side logic required for contact
 * management actions and can be reused across multiple components.
 *
 * @returns {UseContactActionsReturn} useContactActionsResult
 *
 * @returns {boolean} useContactActionsResult.exporting
 * Indicates whether the contact export operation is currently in progress.
 *
 * @returns {Set<string>} useContactActionsResult.archivingContacts
 * A set of contact IDs that are currently being archived.
 * Useful for showing per-row loading indicators or disabling actions.
 *
 * @returns {() => Promise<void>} useContactActionsResult.handleExport
 * Triggers exporting contacts from the server and downloads the file.
 *
 * @returns {(contact: Contact) => Promise<boolean>} useContactActionsResult.handleArchive
 * Archives a single contact.
 * Returns `true` if the operation succeeds, otherwise `false`.
 * @author uplSouvik
 */

export function useContactActions(): UseContactActionsReturn {
  const [exporting, setExporting] = useState(false)
  const [archivingContacts, setArchivingContacts] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  const handleExport = useCallback(
    async (search = "", status = "", source = "", location = "", lists = "", emailStatus = "" ) => {
      try {
        setExporting(true);

        const params = new URLSearchParams();

        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (source) params.append("source", source);
        if (location) params.append("location", location);
        if (lists) params.append("lists", lists);
        if (emailStatus) params.append("emailStatus", emailStatus);

        const blob = await apiFetch(
          `/api/contacts/export?${params.toString()}`,
          { method: "GET", responseType: "blob" }
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `contacts-export.csv`;

        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Contacts exported successfully",
        });
      } catch (e: any) {
        toast({
          title: "Export failed",
          description: e.message || "Failed to export contacts",
          variant: "destructive",
        });
      } finally {
        setExporting(false);
      }
    },
    [toast]
  );

  const handleArchive = useCallback(async (contact: Contact) => {
    try {
      setArchivingContacts(prev => new Set(prev).add(contact.id))

      await apiFetch(`/api/contacts/${contact.id}/manage`, {
        method: "PUT",
        data: { action: "archive" },
      })

      toast({
        title: "Success",
        description: "Contact archived successfully",
      })

      return true
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to archive contact",
        variant: "destructive",
      })
      return false
    } finally {
      setArchivingContacts(prev => {
        const newSet = new Set(prev)
        newSet.delete(contact.id)
        return newSet
      })
    }
  }, [toast])

  return {
    exporting,
    archivingContacts,
    handleExport,
    handleArchive,
  }
}